import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai';
import { Availability_Status_Enum } from '@kheops/graphql';
import { commonSnackbarPropsAtom } from '../../common/state/state';
import { ProductFragment } from '../products';
import { useInsertProductMutation } from '../../mutations/__generated__/insertProduct.generated';
import { PackagingsByCompanyDocument } from '../../queries/__generated__/packagingsByCompany.generated';
import ProductUtils from '../common/products.utils';

export interface EditAvailabilityDialogProps {
  product: ProductFragment;
  baseUnitIndex: number;
  open: boolean;
  onClose: (success?: boolean) => void;
}

export default function EditAvailabilityDialog({ product, baseUnitIndex, open, onClose }: EditAvailabilityDialogProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'products']);
  const [insertProduct, insertProductOutput] = useInsertProductMutation({ refetchQueries: ['ProductVersionById', PackagingsByCompanyDocument] });
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const baseUnit = product.base_units[baseUnitIndex];
  const available = baseUnit.availability === Availability_Status_Enum.Available;

  const handleConfirm = (): void => {
    const productInsertInput = ProductUtils.convertProductFragmentIntoInsertInput(product);

    productInsertInput.base_units!.data[baseUnitIndex].availability = available ? Availability_Status_Enum.Unavailable : Availability_Status_Enum.Available;

    insertProduct({
      variables: { product: productInsertInput },
      update: (cache) => {
        cache.evict({ fieldName: 'product' });
      },
    });
  };

  const handleCancel = (): void => {
    onClose();
  };

  useEffect(() => {
    if (insertProductOutput.data?.insert_product) {
      onClose(true);
      setCommonSnackbarProps({
        label: t('common:update_successful'),
        snackbarProps: {
          open: true,
        },
      });
    }
  }, [insertProductOutput]);

  return (
    <Dialog open={open} onClose={handleCancel} onClick={(event) => event.stopPropagation()}>
      <DialogTitle>
        <Typography>
          {t('products:your_base_unit_availability')}
        </Typography>
        <Typography>{available ? t('products:unavailable_base_unit_short_description') : t('products:your_base_unit_availability_description')}</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="bodyMedium" sx={{ mt: 2, mb: '0.25rem' }}>
          {available ? t('products:confirm_product_unavailable') : t('products:confirm_product_available')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} variant="text">{t('common:cancel')}</Button>
        <Button onClick={handleConfirm} variant="contained" loading={insertProductOutput.loading}>
          {available ? t('products:make_it_unavailable') : t('products:make_it_available')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
